import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useEffect } from 'react';

import { Building07Icon } from '@/components/icons/Building07Icon';
import { EmptyListActionCard } from '@/components/lists/EmptyListActionCard';
import {
  GroupedHeaderList,
  GroupedHeaderListProps,
} from '@/components/lists/GroupedHeaderList/GroupedHeaderList';
import { TableCell } from '@/components/lists/TableCell';
import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import { EmptyListEditEntityActionCard } from '@/modules/entities/components/EmptyListEditEntityActionCard';
import { EntitySubformCategory } from '@/modules/entities/EntitySubforms/entitySubformConfigs';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';

import { EntityDetail_OwnedByStakeFragment } from '../../graphql/EntityDetailPage.generated';
import { getFormattedOwnedByStake } from './BusinessEntityOwnershipList.utils';

type HeaderListItem = ReturnType<typeof getFormattedOwnedByStake>;

export interface BusinessEntityOwnershipListProps
  extends Partial<GroupedHeaderListProps<HeaderListItem>> {
  ownershipAsOfDate?: Date;
  ownedByStakes?: EntityDetail_OwnedByStakeFragment[];
  currentValue?: Decimal;
  onShowEditEntity?: (shouldShowEditButton: boolean) => void;
}

export function BusinessEntityOwnershipList({
  ownedByStakes,
  ownershipAsOfDate,
  currentValue,
  onShowEditEntity,
  ...listProps
}: BusinessEntityOwnershipListProps) {
  const isViewOnly = useViewOnly();

  useEffect(() => {
    if (!isViewOnly && (ownedByStakes || []).length > 0) {
      onShowEditEntity?.(true);
    } else {
      onShowEditEntity?.(false);
    }
  }, [isViewOnly, onShowEditEntity, ownedByStakes]);

  if (isViewOnly && !ownedByStakes?.length) {
    return (
      <EmptyListActionCard
        icon={Building07Icon}
        heading="No ownership information"
        description="No ownership details have been specified for this entity"
        hideButton
      />
    );
  }

  if (!isViewOnly && !ownedByStakes?.length) {
    return (
      <EmptyListEditEntityActionCard
        icon={Building07Icon}
        heading="Specify ownership details"
        buttonText="Add ownership details"
        description="No ownership details have been specified for this entity"
        editSubformCategory={EntitySubformCategory.BUSINESS_ENTITY_DETAILS}
      />
    );
  }

  return (
    <GroupedHeaderList<HeaderListItem>
      rowGroups={[
        {
          heading: compact([
            `Current ownership`,
            ownershipAsOfDate &&
              `(as of ${formatDateToMonDDYYYY(ownershipAsOfDate)})`,
          ]).join(' '),
          noItemsText: 'No ownership information',
          items:
            ownedByStakes?.map((stake) =>
              getFormattedOwnedByStake(stake, currentValue ?? new Decimal(0))
            ) ?? [],
        },
      ]}
      withDividers
      ItemComponent={({ item }) => <TableCell {...item} />}
      {...listProps}
    />
  );
}
